<script>
    import { browser } from '$app/environment';
    import { page } from '$app/stores';
    import Breadcrumbs from '$lib/Breadcrumbs.svelte';
    import BundleUpsellStrip from '$lib/BundleUpsellStrip.svelte';
    import Bundles from '$lib/Bundles.svelte';
    import Button from '$lib/Button.svelte';
    import Icon from '$lib/Icon.svelte';
    import PageLayout from '$lib/PageLayout.svelte';
    import PageSection from '$lib/PageSection.svelte';
    import Prose from '$lib/Prose.svelte';
    import PurchasePaddle from '$lib/PurchasePaddle.svelte';
    import ProductSchema from '$lib/ProductSchema.svelte';
    import ScrollableSection from '$lib/ScrollableSection.svelte';
    import Award from '$lib/Award.svelte';
    import MoneyBack from '$lib/MoneyBack.svelte';
    import Reviews3 from '$lib/Reviews3.svelte';

    import { img_cdn, img_cdn_srcset } from '$lib/utils/image';
    import FAQ2 from '$lib/FAQ2.svelte';
    import Reviews4 from '$lib/Reviews4.svelte';

    $: worksheet = $page.data.worksheet;

    let paddle_el;

    let loading = !browser;

    const image_w = 280;
    const image_h = Math.round(image_w * Math.sqrt(2)); // A4 aspect ratio
</script>

<ProductSchema product={worksheet} reviews={$page.data.reviews} />

<PageLayout>
    <Breadcrumbs />

    <section class="container mx-auto px-4 md:px-8">
        <div
            class="grid gap-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:auto-rows-min lg:items-start lg:gap-x-12 lg:gap-y-6"
        >
            <div>
                <Prose class="lg:mb-0 lg:col-start-1 lg:row-start-1" lg>
                    <h1 class="mb-0!">{worksheet.title}</h1>
                    <p class="lead">{worksheet.lead}</p>
                </Prose>
                
                {#if loading || !worksheet.purchased}
                    <PurchasePaddle
                        product={worksheet}
                        style="small"
                        class="mt-4"
                        bind:this={paddle_el}
                        cta="Buy Now"
                        cta_free="Get for Free"
                        {loading}
                        feature="worksheet-page"
                    />
                {:else if worksheet.files?.length}
                    <div class="flex flex-col gap-4 mt-4 sm:flex-row not-prose">
                        <a
                            href={worksheet.files?.[0]}
                            download
                            class="btn btn-primary-outline btn-xl"
                        >
                            Download All
                        </a>

                        {#if !worksheet.review}
                            <Button
                                route="/worksheets/{worksheet.slug}/review"
                                outline
                                xl
                            >
                                Write a Review
                            </Button>
                        {/if}
                    </div>
                {/if}

                <BundleUpsellStrip
                    product={worksheet}
                    feature="upsell-strip-worksheet-page"
                    class="not-prose mt-6!"
                />
            </div>

            <div
                class="shrink-0 grow-0 w-full lg:max-w-xs lg:col-start-2 lg:row-span-2 xl:max-w-lg 2xl:max-w-2xl"
            >
                <ScrollableSection
                    class="w-full"
                    content_class="flex flex-row gap-4 -mx-4 md:-mx-8"
                >
                    {#each worksheet.preview_images as pi, i}
                        <a
                            href={img_cdn(`/${pi}`)}
                            target="_blank"
                            title="Worksheet lesson ${i + 1}"
                            class="js-thumb-img-link shrink-0"
                        >
                            <img
                                src={img_cdn(`/${pi}`, {
                                    w: image_w,
                                    h: image_h,
                                    fit: 'contain'
                                })}
                                srcset={img_cdn_srcset(`/${pi}`, {
                                    w: image_w,
                                    h: image_h,
                                    fit: 'contain'
                                })}
                                class="object-cover rounded-lg"
                                width={image_w}
                                height={image_h}
                                alt="Worksheet preview image {i + 1}"
                            />
                        </a>
                    {/each}

                    {#if !worksheet.purchased}
                        <button
                            class="stack bg-white text-black aspect-square flex justify-center items-center rounded-lg flex-shrink-0"
                            style={`width:${image_w}px`}
                            on:click={() => {
                                paddle_el.show();
                            }}
                        >
                            <Icon lock_closed />
                        </button>
                    {:else if worksheet.files?.length}
                        <a
                            href={worksheet.files?.[0]}
                            download
                            style={`width:${image_w}px`}
                            class="stack bg-white text-black aspect-square flex flex-col justify-center items-center rounded-lg flex-shrink-0"
                        >
                            <Icon arrow_down_tray />
                            <span>Download</span>
                            <small>.zip</small>
                        </a>
                    {/if}
                </ScrollableSection>

                <div class="mt-8 space-y-4">
                    {#if $page.data.reviews?.length}
                        <Reviews4
                            reviews={$page.data.reviews}
                            class="contents!"
                            review_class=""
                            product={worksheet}
                        />
                    {/if}
                </div>
            </div>

            <Prose class="lg:col-start-1 lg:row-start-2" lg>
                <hr />
                {@html worksheet.description}
            </Prose>
        </div>
    </section>

    {#if $page.data.faq}
        <PageSection class="my-8">
            <FAQ2 />
        </PageSection>
    {/if}

    {#if !worksheet.purchased && worksheet.price}
        <PageSection class="my-12 flex flex-col gap-4 md:gap-6">
            <Bundles
                worksheet_slug={worksheet.slug}
                class="max-w-screen-md"
                feature="bundle-list-worksheet"
            />
        </PageSection>
    {/if}
</PageLayout>

<style>
    .stack {
        position: relative;
        box-shadow: 0 0 0 1px
            color-mix(in oklab, var(--color-gray-900) 20%, transparent);

        &::before,
        &::after {
            background-color: var(--color-gray-300);
            display: block;
            width: 100%;
            height: 100%;
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            transform-origin: bottom center;
            transform: translateY(2%) scale(0.9);
            z-index: -1;
            border-radius: var(--radius-lg);
            box-shadow: 0 0 0 1px
                color-mix(in oklab, var(--color-gray-900) 40%, transparent);
        }
        &::before {
            background-color: var(--color-gray-400);
            transform: translateY(4%) scale(0.8);
        }
    }
</style>
